import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { getData } from "../../../webService/webservice.js";
import { useStore } from "../../../Context/context.jsx";
import { exportDataToExcel } from "../../../utils/excel-Export.js";
import { getDateRange } from "../../../utils/get-DateRange.js";
import "../../../styles/Auth/sales-report.css";

const mapQueryToDateRangeLabel = (paramRange) => {
    if (!paramRange) return null;
    const lower = paramRange.toLowerCase();
    if (lower === 'today') return 'Today';
    if (lower === 'last7days' || lower === 'weekly' || lower === 'week' || lower === 'last_7_days') return 'Last 7 Days';
    if (lower === 'currentmonth' || lower === 'monthly' || lower === 'month' || lower === 'current_month') return 'Current Month';
    if (lower === 'currentfy' || lower === 'yearly' || lower === 'year' || lower === 'current_fy') return 'Current FY';
    if (lower === 'custom') return 'Custom';
    return paramRange;
};

const StaffSalesReport = () => {
    const location = useLocation();
    const today = new Date().toISOString().split("T")[0];
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [dateRange, setDateRange] = useState("Today");

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const paramDateRange = queryParams.get("dateRange");
        const paramFromDate = queryParams.get("fromDate");
        const paramToDate = queryParams.get("toDate");

        if (paramDateRange) {
            const mappedRange = mapQueryToDateRangeLabel(paramDateRange);
            if (mappedRange === "Custom") {
                setDateRange("Custom");
                if (paramFromDate && paramToDate) {
                    setStartDate(paramFromDate);
                    setEndDate(paramToDate);
                }
            } else if (mappedRange) {
                setDateRange(mappedRange);
                const { startDate: s, endDate: eD } = getDateRange(mappedRange);
                setStartDate(s);
                setEndDate(eD);
            }
        }
    }, [location.search]);

    const { selectedStoreId } = useStore();
    const sessionToken = localStorage.getItem("Token");
    const role = localStorage.getItem("role");
    const adminStoreIds = JSON.parse(localStorage.getItem("storeIds") || "[]");

    // --- State Management ---
    const [activeTab, setActiveTab] = useState("staff-summary");
    const [loading, setLoading] = useState(false);

    // View Modal
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState({
        totalRevenue: 0,
        totalServiceSales: 0,
        totalProductSales: 0,
        totalDiscount: 0
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Filters
    const [filters, setFilters] = useState({
        staffId: "",
        storeId: selectedStoreId || "",
        serviceCategoryId: "",
        serviceId: "",
        productId: "",
        subscriptionId: ""
    });

    // Table Controls
    const [tableSearch, setTableSearch] = useState("");
    const [showColumnConfig, setShowColumnConfig] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState({
        staffName: true,
        branch: true,
        servicesProvided: true,
        productsSold: true,
        serviceRevenue: true,
        productRevenue: true,
        membershipsSold: true,
        subscriptionRevenue: true,
        discountGiven: true,
        totalRevenue: true,
        action: true,
        serviceName: true,
        categoryName: true,
        productName: true,
        soldCount: true,
        totalAmount: true
    });

    const toggleColumn = (col) => {
        setVisibleColumns(prev => ({ ...prev, [col]: !prev[col] }));
    };

    const TAB_COLUMNS = {
        "staff-summary": ["staffName", "branch", "servicesProvided", "productsSold", "membershipsSold", "serviceRevenue", "productRevenue", "subscriptionRevenue", "discountGiven", "totalRevenue", "action"],
        "service-wise": ["staffName", "branch", "serviceName", "categoryName", "soldCount", "totalAmount", "action"],
        "product-wise": ["staffName", "branch", "productName", "soldCount", "totalAmount", "action"],
        "membership-wise": [
            "staffName",
            "branch",
            "membershipName",
            "soldCount",
            "totalAmount",
            "action"
        ]
    };

    const COLUMN_LABELS = {
        staffName: "Staff Name",
        branch: "Branch",
        servicesProvided: "Services Provided",
        productsSold: "Products Sold",
        serviceRevenue: "Service Revenue",
        productRevenue: "Product Revenue",
        membershipsSold: "Memberships Sold",
        subscriptionRevenue: "Subscription Revenue",
        discountGiven: "Discount Given",
        totalRevenue: "Total Revenue",
        serviceName: "Service Name",
        categoryName: "Category Name",
        productName: "Product Name",
        membershipName: "Membership Name",
        soldCount: "Sold Count",
        totalAmount: "Total Amount",
        action: "Action"
    };

    const columnConfigRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (columnConfigRef.current && !columnConfigRef.current.contains(event.target)) {
                setShowColumnConfig(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Dropdown Lists
    const [staffList, setStaffList] = useState([]);
    const [storeList, setStoreList] = useState([]);
    const [serviceCategoryList, setServiceCategoryList] = useState([]);
    const [serviceList, setServiceList] = useState([]);
    const [productList, setProductList] = useState([]);
    const [subscriptionList, setSubscriptionList] = useState([]);
    const safeExtractArray = (res) => {
        if (!res || !res.data) return [];
        if (Array.isArray(res.data) && res.data[0]?.paginatedResults) return res.data[0].paginatedResults;
        if (Array.isArray(res.data)) return res.data;
        if (res.data.results) {
            if (Array.isArray(res.data.results)) {
                if (res.data.results[0]?.paginatedResults) return res.data.results[0].paginatedResults;
                return res.data.results;
            }
        }
        if (typeof res.data === 'object' && !Array.isArray(res.data)) {
            for (let key in res.data) {
                if (Array.isArray(res.data[key])) return res.data[key];
            }
        }
        return [];
    };

    const fetchDropdownData = async (targetStoreId) => {
        try {
            const commonParams = { limit: 10000 };
            if (targetStoreId) commonParams.storeId = targetStoreId;

            const [staffRes, storeRes, sCatRes, serviceRes, prodRes, subRes] = await Promise.all([
                getData("users/users", commonParams, sessionToken),
                getData("stores/stores", { limit: 10000 }, sessionToken),
                getData("servicecategories/service-categories", commonParams, sessionToken),
                getData("services/services", commonParams, sessionToken),
                getData("products/products", commonParams, sessionToken),
                getData("subscriptions/subscriptions", commonParams, sessionToken)
            ]);

            setStaffList(safeExtractArray(staffRes));
            setStoreList(safeExtractArray(storeRes));
            setServiceCategoryList(safeExtractArray(sCatRes));
            setServiceList(safeExtractArray(serviceRes));
            setProductList(safeExtractArray(prodRes));
            setSubscriptionList(safeExtractArray(subRes));
        } catch (error) {
            console.error("Error fetching dropdown data:", error);
        }
    };

    const fetchStaffSalesReport = async () => {
        try {
            setLoading(true);
            const params = {
                startDate,
                endDate,
                tab: activeTab,
                page: currentPage,
                limit: rowsPerPage,
                ...filters
            };

            if (!params.storeId && selectedStoreId) {
                params.storeId = selectedStoreId;
            }

            const response = await getData("reports/reports/staff-sales", params, sessionToken);

            if (response?.status === 200 || response?.success) {
                setData(response.data?.data || []);
                setSummary(response.data?.summary || {
                    totalRevenue: 0,
                    totalServiceSales: 0,
                    totalProductSales: 0,
                    avgCommission: 0,
                    totalDiscount: 0
                });

                if (response.data?.pagination) {
                    const { totalItems: ti, totalPages: tp } = response.data.pagination;
                    setTotalItems(ti);
                    setTotalPages(tp);
                }
            } else {
                toast.error(response?.message || "Failed to fetch staff sales report");
            }
        } catch (error) {
            console.error("Error fetching staff sales report:", error);
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDropdownData(filters.storeId || selectedStoreId);
    }, [filters.storeId, selectedStoreId]);

    useEffect(() => {
        setCurrentPage(1);
    }, [startDate, endDate, activeTab]);

    useEffect(() => {
        fetchStaffSalesReport();
    }, [
        startDate,
        endDate,
        activeTab,
        selectedStoreId,
        currentPage,
        rowsPerPage,

        filters.staffId,
        filters.storeId,
        filters.serviceCategoryId,
        filters.serviceId,
        filters.productId,
        filters.subscriptionId
    ]);

    // --- Styles for React Select ---
    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            height: '44px',
            minHeight: '44px',
            background: '#f8fafc',
            borderColor: state.isFocused ? '#3c0008' : '#e2e8f0',
            borderRadius: '12px',
            padding: '0 4px',
            fontSize: '14px',
            color: '#1e293b',
            boxShadow: 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#3c0008' : '#cbd5e1',
            }
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 8px',
        }),
        singleValue: (base) => ({
            ...base,
            color: '#1e293b',
        }),
        placeholder: (base) => ({
            ...base,
            color: '#64748b',
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999
        }),
        menu: (base) => ({
            ...base,
            zIndex: 90,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        }),
        option: (base, state) => ({
            ...base,
            fontSize: '14px',
            backgroundColor: state.isSelected ? '#3c0008' : state.isFocused ? '#f1f5f9' : '#fff',
            color: state.isSelected ? '#fff' : '#1e293b',
            '&:active': {
                backgroundColor: '#3c0008',
                color: '#fff',
            }
        })
    };

    const handleFilterChange = (e) => {

        const { name, value } = e.target;

        setCurrentPage(1);

        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const filteredData = data.filter(item => {
        if (!tableSearch) return true;
        const search = tableSearch.toLowerCase();
        return (
            (item.staffName && item.staffName.toLowerCase().includes(search)) ||
            (item.staffCode && item.staffCode.toLowerCase().includes(search)) ||
            (item.serviceName && item.serviceName.toLowerCase().includes(search)) ||
            (item.productName && item.productName.toLowerCase().includes(search))
        );
    });

    // Calculate table totals
    const getTableTotals = () => {
        let totalServices = 0;
        let totalProducts = 0;
        let totalMemberships = 0;
        let totalServiceRevenue = 0;
        let totalProductRevenue = 0;
        let totalSubscriptionRevenue = 0;
        let totalDiscountGiven = 0;
        let totalRevenue = 0;
        let totalAmount = 0;

        if (activeTab === "staff-summary") {
            filteredData.forEach(item => {
                totalServices += Number(item.servicesProvided) || 0;
                totalProducts += Number(item.productsSold) || 0;
                totalMemberships += Number(item.membershipsSold) || 0;
                totalServiceRevenue += Number(item.serviceRevenue) || 0;
                totalProductRevenue += Number(item.productRevenue) || 0;
                totalSubscriptionRevenue += Number(item.subscriptionRevenue) || 0;
                totalDiscountGiven += Number(item.discountGiven) || 0;
                totalRevenue += Number(item.totalRevenue) || 0;
            });
        } else if (activeTab === "service-wise") {
            filteredData.forEach(item => {
                totalServices += Number(item.soldCount) || 0;
                totalAmount += Number(item.totalAmount) || 0;
            });
        } else if (activeTab === "product-wise") {
            filteredData.forEach(item => {
                totalProducts += Number(item.soldCount) || 0;
                totalAmount += Number(item.totalAmount) || 0;
            });
        }
        else if (activeTab === "membership-wise") {

            filteredData.forEach(item => {

                totalMemberships +=
                    Number(item.soldCount) || 0;

                totalAmount +=
                    Number(item.totalAmount) || 0;
            });
        }

        return {
            totalServices,
            totalProducts,
            totalMemberships,
            totalServiceRevenue,
            totalProductRevenue,
            totalSubscriptionRevenue,
            totalDiscountGiven,
            totalRevenue,
            totalAmount
        };
    };

    const totals = getTableTotals();

    const resetFilters = async () => {
        const todayStr = new Date().toISOString().split("T")[0];

        const resetData = {
            staffId: "",
            storeId: selectedStoreId || "",
            serviceCategoryId: "",
            serviceId: "",
            productId: "",
            subscriptionId: "",
        };

        setStartDate(todayStr);
        setEndDate(todayStr);
        setDateRange("Today");
        setFilters(resetData);
        setCurrentPage(1);
        setTableSearch("");

        try {
            setLoading(true);
            const params = {
                startDate: todayStr,
                endDate: todayStr,
                tab: activeTab,
                page: 1,
                limit: rowsPerPage,
                ...resetData
            };

            const response = await getData("reports/reports/staff-sales", params, sessionToken);

            if (response?.status === 200 || response?.success) {
                setData(response.data?.data || []);
                setSummary(response.data?.summary || {
                    totalRevenue: 0,
                    totalServiceSales: 0,
                    totalProductSales: 0,
                    avgCommission: 0,
                    totalDiscount: 0
                });

                if (response.data?.pagination) {
                    const { totalItems: ti, totalPages: tp } = response.data.pagination;
                    setTotalItems(ti);
                    setTotalPages(tp);
                }
            } else {
                toast.error(response?.message || "Failed to fetch staff sales report");
            }
        } catch (error) {
            console.error("Error fetching staff sales report:", error);
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const clearTableSearch = () => {
        setTableSearch("");
        setShowColumnConfig(false);
    };

    const handleViewInvoice = (row) => {
        setViewModalOpen(true);
        setViewData(row);
    };

    const handleExport = () => {

        if (!data.length) {
            return toast.info("No data to export");
        }

        let exportData = [];

        switch (activeTab) {

            case "staff-summary":

                exportData = data.map((item) => ({
                    "Staff Name": item.staffName || "",
                    "Branch": item.branch || "",
                    "Services Provided": item.servicesProvided || 0,
                    "Products Sold": item.productsSold || 0,
                    "Memberships Sold": item.membershipsSold || 0,
                    "Service Revenue": item.serviceRevenue || 0,
                    "Product Revenue": item.productRevenue || 0,
                    "Membership Revenue": item.subscriptionRevenue || 0,
                    "Discount Given": item.discountGiven || 0,
                    "Total Revenue": item.totalRevenue || 0
                }));

                exportData.push({});
                exportData.push({
                    "Staff Name": "TOTAL",
                    "Services Provided": totals.totalServices,
                    "Products Sold": totals.totalProducts,
                    "Memberships Sold": totals.totalMemberships,
                    "Service Revenue": totals.totalServiceRevenue,
                    "Product Revenue": totals.totalProductRevenue,
                    "Membership Revenue": totals.totalSubscriptionRevenue,
                    "Discount Given": totals.totalDiscountGiven,
                    "Total Revenue": totals.totalRevenue
                });

                break;

            case "service-wise":

                exportData = data.map((item) => ({
                    "Staff Name": item.staffName || "",
                    "Branch": item.branch || "",
                    "Service Name": item.serviceName || "",
                    "Category Name": item.categoryName || "",
                    "Sold Count": item.soldCount || 0,
                    "Total Revenue": item.totalAmount || 0
                }));

                exportData.push({});
                exportData.push({
                    "Staff Name": "TOTAL",
                    "Sold Count": totals.totalServices,
                    "Total Revenue": totals.totalAmount
                });

                break;

            case "product-wise":

                exportData = data.map((item) => ({
                    "Staff Name": item.staffName || "",
                    "Branch": item.branch || "",
                    "Product Name": item.productName || "",
                    "Sold Count": item.soldCount || 0,
                    "Total Revenue": item.totalAmount || 0
                }));

                exportData.push({});
                exportData.push({
                    "Staff Name": "TOTAL",
                    "Sold Count": totals.totalProducts,
                    "Total Revenue": totals.totalAmount
                });

                break;

            case "membership-wise":

                exportData = data.map((item) => ({
                    "Staff Name": item.staffName || "",
                    "Branch": item.branch || "",
                    "Membership Name": item.membershipName || "",
                    "Sold Count": item.soldCount || 0,
                    "Total Revenue": item.totalAmount || 0
                }));

                exportData.push({});
                exportData.push({
                    "Staff Name": "TOTAL",
                    "Sold Count": totals.totalMemberships,
                    "Total Revenue": totals.totalAmount
                });

                break;

            default:
                exportData = data;
        }

        const fileName =
            `Staff_Sales_Report_${activeTab}_${startDate}_to_${endDate}`;

        exportDataToExcel(
            exportData,
            "Staff Sales Report",
            `${fileName}.xlsx`
        );
    };
    const isSuperAdmin = role === "SuperAdmin" || role === "SelfAdmin";
    const filteredStoreList = isSuperAdmin ? storeList : storeList.filter(s => adminStoreIds.includes(s._id));
    const showBranchFilter = isSuperAdmin || filteredStoreList.length > 1;

    const formatAmount = (amount) => {
        return Number(amount || 0).toFixed(2);
    };

    return (
        <div className="product-layout">
            <div className="product-content">
                <main className="product-main">
                    <div className="sales-report-container">
                        <div className="sales-report-standalone-header">
                            <div className="header-left">
                                <h1 className="page-title">Staff Sales Report</h1>
                                <p className="page-subtitle">Track and analyze sales performance by staff member</p>
                            </div>
                            <div className="header-right">
                                <button className="export-dropdown-btn" onClick={handleExport}>
                                    <Icon icon="mdi:export-variant" width="18" /> Export <Icon icon="mdi:chevron-down" />
                                </button>
                            </div>
                        </div>

                        <div className="sales-report-tabs-wrapper">
                            <div className="sales-report-tabs">
                                <button
                                    className={`sales-report-tab ${activeTab === "staff-summary" ? "active" : ""}`}
                                    onClick={() => setActiveTab("staff-summary")}
                                >
                                    Staff Summary
                                </button>
                                <button
                                    className={`sales-report-tab ${activeTab === "service-wise" ? "active" : ""}`}
                                    onClick={() => setActiveTab("service-wise")}
                                >
                                    Service Sales by Staff
                                </button>
                                <button
                                    className={`sales-report-tab ${activeTab === "product-wise" ? "active" : ""}`}
                                    onClick={() => setActiveTab("product-wise")}
                                >
                                    Product Sales by Staff
                                </button>
                                <button
                                    className={`sales-report-tab ${activeTab === "membership-wise" ? "active" : ""}`}
                                    onClick={() => {
                                        setCurrentPage(1);
                                        setActiveTab("membership-wise");
                                    }}
                                >
                                    Membership Sales by Staff
                                </button>
                            </div>
                        </div>

                        <div className="sales-filter-bar">
                            <div className="filter-grid">
                                <div className="filter-item">
                                    <label>Date Range</label>
                                    <div className="date-range-picker-combined">
                                        <select
                                            className="filter-select date-range-select"
                                            value={dateRange}
                                            onChange={e => {
                                                const range = e.target.value;
                                                setDateRange(range);
                                                const { startDate: s, endDate: eD } = getDateRange(range, startDate, endDate);
                                                setStartDate(s);
                                                setEndDate(eD);
                                            }}
                                        >
                                            <option value="Today">Today</option>
                                            <option value="Yesterday">Yesterday</option>
                                            <option value="Last 7 Days">Last 7 Days</option>
                                            <option value="Last Month">Last Month</option>
                                            <option value="Current Month">Current Month</option>
                                            <option value="Current FY">Current Financial Year</option>
                                            <option value="Previous FY">Previous Financial Year</option>
                                            <option value="Custom">Custom Date</option>
                                        </select>
                                        {dateRange === "Custom" ? (
                                            <div className="custom-date-inputs">
                                                <input
                                                    type="date"
                                                    className="filter-input date-input"
                                                    value={startDate}
                                                    onChange={(e) => setStartDate(e.target.value)}
                                                />
                                                <span className="date-separator">to</span>
                                                <input
                                                    type="date"
                                                    className="filter-input date-input"
                                                    value={endDate}
                                                    onChange={(e) => setEndDate(e.target.value)}
                                                />
                                            </div>
                                        ) : (
                                            <div className="date-display-box">
                                                <Icon icon="mdi:calendar-outline" width="18" />
                                                <span>{startDate === endDate ? startDate : `${startDate} - ${endDate}`}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="filter-item">
                                    <label>Staff Name</label>
                                    <Select
                                        name="staffId"
                                        value={filters.staffId ? { value: filters.staffId, label: staffList.find(s => s._id === filters.staffId)?.name || "Unknown" } : null}
                                        onChange={(selected) => handleFilterChange({ target: { name: "staffId", value: selected ? selected.value : "" } })}
                                        options={[
                                            { value: "", label: "All Staff" },
                                            ...(Array.isArray(staffList) ? staffList.map(s => ({ value: s._id, label: s.name })) : [])
                                        ]}
                                        isClearable
                                        placeholder="Search Staff Name..."
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        styles={customSelectStyles}
                                    />
                                </div>

                                {showBranchFilter && (
                                    <div className="filter-item">
                                        <label>Branch</label>
                                        <select name="storeId" value={filters.storeId} onChange={handleFilterChange} className="filter-select">
                                            <option value="">All Branches</option>
                                            {filteredStoreList.map(s => (
                                                <option key={s._id} value={s._id}>
                                                    {s.branchName || s.storeName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {(activeTab === "service-wise") && (
                                    <div className="filter-item">
                                        <label>Service</label>
                                        <Select
                                            name="serviceId"
                                            value={filters.serviceId ? { value: filters.serviceId, label: serviceList.find(s => s._id === filters.serviceId)?.serviceName || "Unknown" } : null}
                                            onChange={(selected) => handleFilterChange({ target: { name: "serviceId", value: selected ? selected.value : "" } })}
                                            options={[
                                                { value: "", label: "All Services" },
                                                ...(Array.isArray(serviceList) ? serviceList.map(s => ({ value: s._id, label: s.serviceName })) : [])
                                            ]}
                                            isClearable
                                            placeholder="Search Service..."
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            styles={customSelectStyles}
                                        />
                                    </div>
                                )}
                                {(activeTab === "product-wise") && (
                                    <div className="filter-item">
                                        <label>Product</label>

                                        <Select
                                            name="productId"

                                            value={
                                                filters.productId
                                                    ? {
                                                        value: filters.productId,
                                                        label:
                                                            productList.find(
                                                                p =>
                                                                    p._id ===
                                                                    filters.productId
                                                            )?.title || "Unknown"
                                                    }
                                                    : null
                                            }

                                            onChange={(selected) =>
                                                handleFilterChange({
                                                    target: {
                                                        name: "productId",
                                                        value:
                                                            selected
                                                                ? selected.value
                                                                : ""
                                                    }
                                                })
                                            }

                                            options={[
                                                {
                                                    value: "",
                                                    label: "All Products"
                                                },

                                                ...(Array.isArray(productList)
                                                    ? productList.map(p => ({
                                                        value: p._id,
                                                        label: p.title
                                                    }))
                                                    : [])
                                            ]}

                                            isClearable

                                            placeholder="Search Product..."

                                            className="react-select-container"
                                            classNamePrefix="react-select"

                                            styles={customSelectStyles}
                                        />
                                    </div>
                                )}

                                {(activeTab === "membership-wise") && (
                                    <div className="filter-item">

                                        <label>Membership</label>

                                        <Select
                                            name="subscriptionId"

                                            value={
                                                filters.subscriptionId
                                                    ? {
                                                        value: filters.subscriptionId,

                                                        label:
                                                            subscriptionList.find(
                                                                s =>
                                                                    s._id ===
                                                                    filters.subscriptionId
                                                            )?.subscriptionName || "Unknown"
                                                    }
                                                    : null
                                            }

                                            onChange={(selected) =>
                                                handleFilterChange({
                                                    target: {
                                                        name: "subscriptionId",
                                                        value:
                                                            selected
                                                                ? selected.value
                                                                : ""
                                                    }
                                                })
                                            }

                                            options={[
                                                {
                                                    value: "",
                                                    label: "All Memberships"
                                                },

                                                ...(Array.isArray(subscriptionList)
                                                    ? subscriptionList.map(s => ({
                                                        value: s._id,
                                                        label: s.subscriptionName
                                                    }))
                                                    : [])
                                            ]}

                                            isClearable

                                            placeholder="Search Membership..."

                                            className="react-select-container"
                                            classNamePrefix="react-select"

                                            styles={customSelectStyles}
                                        />
                                    </div>
                                )}

                                <div className="filter-actions-combined">
                                    <button className="btn-reset-filters" onClick={resetFilters}>Reset</button>
                                </div>
                            </div>
                        </div>

                        <div className="sales-summary-grid">
                            <div className="sales-summary-card card-revenue">

                                <div className="sales-card-icon">

                                    <Icon
                                        icon={
                                            activeTab === "membership-wise"
                                                ? "mdi:card-account-details-outline"
                                                : activeTab === "product-wise"
                                                    ? "mdi:cart-outline"
                                                    : activeTab === "service-wise"
                                                        ? "mdi:briefcase-outline"
                                                        : "mdi:cash-multiple"
                                        }
                                    />

                                </div>

                                <div className="sales-card-info">

                                    <span className="sales-card-label">

                                        {
                                            activeTab === "membership-wise"
                                                ? "Total Membership Revenue"

                                                : activeTab === "product-wise"
                                                    ? "Total Product Revenue"

                                                    : activeTab === "service-wise"
                                                        ? "Total Service Revenue"

                                                        : "Total Revenue"
                                        }

                                    </span>

                                    <span className="sales-card-value">

                                        {(

                                            activeTab === "membership-wise"
                                                ? summary.subscriptionRevenue || 0

                                                : activeTab === "product-wise"
                                                    ? formatAmount(summary.totalProductSales || 0)

                                                    : activeTab === "service-wise"
                                                        ? formatAmount(summary.totalServiceSales || 0)

                                                        : formatAmount(summary.totalRevenue || 0)
                                        )}

                                    </span>

                                </div>

                            </div>
                            {(activeTab === "service-wise" || activeTab === "staff-summary") && (
                                <div className="sales-summary-card card-avg">
                                    <div className="sales-card-icon">
                                        <Icon icon="mdi:tune-vertical-variant" />
                                    </div>
                                    <div className="sales-card-info">
                                        <span className="sales-card-label">Total Service Sales</span>
                                        <span className="sales-card-value">{formatAmount(summary.totalServiceSales || 0)}</span>
                                    </div>
                                </div>
                            )}
                            {(activeTab === "product-wise" || activeTab === "staff-summary") && (
                                <div className="sales-summary-card card-bills">
                                    <div className="sales-card-icon">
                                        <Icon icon="mdi:cart-outline" />
                                    </div>
                                    <div className="sales-card-info">
                                        <span className="sales-card-label">Total Product Sales</span>
                                        <span className="sales-card-value">{formatAmount(summary.totalProductSales || 0)}</span>
                                    </div>
                                </div>
                            )}
                            {(activeTab === "membership-wise" || activeTab === "staff-summary") && (
                                <div className="sales-summary-card card-bills">
                                    <div className="sales-card-icon">
                                        <Icon icon="mdi:card-account-details-outline" />
                                    </div>
                                    <div className="sales-card-info">
                                        <span className="sales-card-label">Total Membership Sales</span>
                                        <span className="sales-card-value">{formatAmount(summary.subscriptionRevenue || 0)}</span>
                                    </div>
                                </div>
                            )}
                            {(activeTab === "service-wise" || activeTab === "product-wise" || activeTab === "staff-summary") && (
                                <div className="sales-summary-card card-discount">
                                    <div className="sales-card-icon">
                                        <Icon icon="mdi:tag-outline" />
                                    </div>
                                    <div className="sales-card-info">
                                        <span className="sales-card-label">Total Discount Given</span>
                                        <span className="sales-card-value">{formatAmount(summary.totalDiscount || 0)}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="sales-table-container">
                            <div className="table-header-row">
                                <h3 className="table-title">Staff Performance Details</h3>
                                <div className="table-controls">
                                    <div className="table-search-box">
                                        <input
                                            type="text"
                                            placeholder="Search in table..."
                                            value={tableSearch}
                                            onChange={(e) => setTableSearch(e.target.value)}
                                        />
                                        <Icon icon="mdi:magnify" />
                                    </div>
                                    <button className="table-control-btn" onClick={clearTableSearch}>
                                        <Icon icon="mdi:filter-remove-outline" /> Clear
                                    </button>
                                    <div className="column-config-wrapper" ref={columnConfigRef}>
                                        <button
                                            className="table-control-btn"
                                            onClick={() => setShowColumnConfig(!showColumnConfig)}
                                        >
                                            <Icon icon="mdi:view-column-outline" /> Columns
                                        </button>
                                        {showColumnConfig && (
                                            <div className="column-config-dropdown">
                                                <div className="dropdown-header">Toggle Columns</div>
                                                {(TAB_COLUMNS[activeTab] || []).map(col => (
                                                    <label key={col} className="column-config-item">
                                                        <input
                                                            type="checkbox"
                                                            checked={visibleColumns[col]}
                                                            onChange={() => toggleColumn(col)}
                                                        />
                                                        <span>{COLUMN_LABELS[col]}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="sales-table-wrapper">
                                {loading ? (
                                    <div className="loading-state">
                                        <Icon icon="mdi:loading" className="spin" />
                                        <p>Loading sales data...</p>
                                    </div>
                                ) : data.length > 0 ? (
                                    <table className="sales-data-table">
                                        <thead>
                                            <tr>
                                                {(TAB_COLUMNS[activeTab] || []).map(col => visibleColumns[col] && (
                                                    <th key={col}>{COLUMN_LABELS[col]}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredData.map((row, idx) => (
                                                <tr key={idx}>
                                                    {activeTab === "staff-summary" ? (
                                                        <>
                                                            {visibleColumns.staffName && <td>{row.staffName}</td>}
                                                            {visibleColumns.branch && <td>{row.branch}</td>}
                                                            {visibleColumns.servicesProvided && <td>{row.servicesProvided}</td>}
                                                            {visibleColumns.productsSold && <td>{row.productsSold}</td>}
                                                            {visibleColumns.membershipsSold && (
                                                                <td>{row.membershipsSold || 0}</td>
                                                            )}
                                                            {visibleColumns.serviceRevenue && <td>{formatAmount(row.serviceRevenue)}</td>}
                                                            {visibleColumns.productRevenue && <td>{formatAmount(row.productRevenue)}</td>}
                                                            {visibleColumns.subscriptionRevenue && (
                                                                <td>{formatAmount(row.subscriptionRevenue || 0)}</td>
                                                            )}
                                                            {visibleColumns.discountGiven && <td>{formatAmount(row.discountGiven)}</td>}
                                                            {visibleColumns.totalRevenue && <td className="total-amount-cell">{formatAmount(row.totalRevenue)}</td>}
                                                            {visibleColumns.action && (
                                                                <td>
                                                                    <button
                                                                        className="action-view-btn"
                                                                        title="View Details"
                                                                        onClick={() => handleViewInvoice(row)}
                                                                    >
                                                                        <Icon icon="mdi:eye-outline" />
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </>
                                                    ) : activeTab === "service-wise" ? (
                                                        <>
                                                            {visibleColumns.staffName && <td>{row.staffName}</td>}
                                                            {visibleColumns.branch && <td>{row.branch}</td>}
                                                            {visibleColumns.serviceName && <td style={{ fontWeight: 600 }}>{row.serviceName}</td>}
                                                            {visibleColumns.categoryName && <td>{row.categoryName}</td>}
                                                            {visibleColumns.soldCount && <td>{row.soldCount}</td>}
                                                            {visibleColumns.totalAmount && <td style={{ fontWeight: 700 }}>{formatAmount(row.totalAmount)}</td>}
                                                            {visibleColumns.action && (
                                                                <td>
                                                                    <button
                                                                        className="action-view-btn"
                                                                        title="View Details"
                                                                        onClick={() => handleViewInvoice(row)}
                                                                    >
                                                                        <Icon icon="mdi:eye-outline" />
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </>
                                                    ) : activeTab === "membership-wise" ? (
                                                        <>
                                                            {visibleColumns.staffName && (
                                                                <td>{row.staffName}</td>
                                                            )}

                                                            {visibleColumns.branch && (
                                                                <td>{row.branch}</td>
                                                            )}

                                                            {visibleColumns.membershipName && (
                                                                <td style={{ fontWeight: 600, color: "#7c3aed" }}>
                                                                    {row.membershipName}
                                                                </td>
                                                            )}

                                                            {visibleColumns.soldCount && (
                                                                <td>{row.soldCount}</td>
                                                            )}

                                                            {visibleColumns.totalAmount && (
                                                                <td style={{ fontWeight: 700 }}>
                                                                    {formatAmount(row.totalAmount || 0)}
                                                                </td>
                                                            )}

                                                            {visibleColumns.action && (
                                                                <td>
                                                                    <button
                                                                        className="action-view-btn"
                                                                        onClick={() => handleViewInvoice(row)}
                                                                    >
                                                                        <Icon icon="mdi:eye-outline" />
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </>
                                                    ) : activeTab === "product-wise" ? (
                                                        <>
                                                            {visibleColumns.staffName && (
                                                                <td>{row.staffName}</td>
                                                            )}

                                                            {visibleColumns.branch && (
                                                                <td>{row.branch}</td>
                                                            )}

                                                            {visibleColumns.productName && (
                                                                <td style={{ fontWeight: 600 }}>
                                                                    {row.productName}
                                                                </td>
                                                            )}

                                                            {visibleColumns.soldCount && (
                                                                <td>{row.soldCount}</td>
                                                            )}

                                                            {visibleColumns.totalAmount && (
                                                                <td style={{ fontWeight: 700 }}>
                                                                    {formatAmount(row.totalAmount || 0)}
                                                                </td>
                                                            )}

                                                            {visibleColumns.action && (
                                                                <td>
                                                                    <button
                                                                        className="action-view-btn"
                                                                        onClick={() => handleViewInvoice(row)}
                                                                    >
                                                                        <Icon icon="mdi:eye-outline" />
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </>
                                                    ) : null}
                                                </tr>
                                            ))}
                                        </tbody>
                                        {filteredData.length > 0 && (
                                            <tfoot>
                                                <tr>
                                                    {(TAB_COLUMNS[activeTab] || [])
                                                        .filter(col => visibleColumns[col])
                                                        .map((col, index) => {
                                                            const isFirst = index === 0;
                                                            if (isFirst) {
                                                                return (
                                                                    <td key={col} className="table-total-label">
                                                                        <strong>Total</strong>
                                                                    </td>
                                                                );
                                                            }

                                                            if (activeTab === "staff-summary") {
                                                                if (col === "servicesProvided") {
                                                                    return (
                                                                        <td key={col} className="table-total-value">
                                                                            <span className="service-cell">{formatAmount(totals.totalServices)}</span>
                                                                        </td>
                                                                    );
                                                                }
                                                                if (col === "productsSold") {
                                                                    return (
                                                                        <td key={col} className="table-total-value">
                                                                            <strong>{formatAmount(totals.totalProducts)}</strong>
                                                                        </td>
                                                                    );
                                                                }
                                                                if (col === "membershipsSold") {
                                                                    return (
                                                                        <td key={col} className="table-total-value">
                                                                            <strong>{formatAmount(totals.totalMemberships)}</strong>
                                                                        </td>
                                                                    );
                                                                }

                                                                if (col === "serviceRevenue") {
                                                                    return (
                                                                        <td key={col} className="table-total-value">
                                                                            <strong>{formatAmount(totals.totalServiceRevenue)}</strong>
                                                                        </td>
                                                                    );
                                                                }
                                                                if (col === "productRevenue") {
                                                                    return (
                                                                        <td key={col} className="table-total-value">
                                                                            <strong>{formatAmount(totals.totalProductRevenue)}</strong>
                                                                        </td>
                                                                    );
                                                                }
                                                                if (col === "discountGiven") {
                                                                    return (
                                                                        <td key={col} className="table-total-value">
                                                                            <strong>{formatAmount(totals.totalDiscountGiven)}</strong>
                                                                        </td>
                                                                    );
                                                                }
                                                                if (col === "subscriptionRevenue") {
                                                                    return (
                                                                        <td key={col} className="table-total-value">
                                                                            <strong>
                                                                                {formatAmount(
                                                                                    totals.totalSubscriptionRevenue
                                                                                )}
                                                                            </strong>
                                                                        </td>
                                                                    );
                                                                }
                                                                if (col === "totalRevenue") {
                                                                    return (
                                                                        <td key={col} className="table-total-value total-amount-cell">
                                                                            {formatAmount(totals.totalRevenue)}
                                                                        </td>
                                                                    );
                                                                }
                                                            } else if (activeTab === "service-wise") {
                                                                if (col === "soldCount") {
                                                                    return (
                                                                        <td key={col} className="table-total-value">
                                                                            <strong>{formatAmount(totals.totalServices)}</strong>
                                                                        </td>
                                                                    );
                                                                }
                                                                if (col === "totalAmount") {
                                                                    return (
                                                                        <td key={col} className="table-total-value" style={{ fontWeight: 700 }}>
                                                                            {formatAmount(totals.totalAmount)}
                                                                        </td>
                                                                    );
                                                                }
                                                            } else if (activeTab === "product-wise") {
                                                                if (col === "soldCount") {
                                                                    return (
                                                                        <td key={col} className="table-total-value">
                                                                            <strong>{formatAmount(totals.totalProducts)}</strong>
                                                                        </td>
                                                                    );
                                                                }
                                                                if (col === "totalAmount") {
                                                                    return (
                                                                        <td key={col} className="table-total-value" style={{ fontWeight: 700 }}>
                                                                            {formatAmount(totals.totalAmount)}
                                                                        </td>
                                                                    );
                                                                }
                                                            } else if (activeTab === "membership-wise") {

                                                                if (col === "soldCount") {

                                                                    return (
                                                                        <td
                                                                            key={col}
                                                                            className="table-total-value"
                                                                        >
                                                                            <strong>
                                                                                {formatAmount(totals.totalMemberships)}
                                                                            </strong>
                                                                        </td>
                                                                    );
                                                                }

                                                                if (col === "totalAmount") {

                                                                    return (
                                                                        <td
                                                                            key={col}
                                                                            className="table-total-value"
                                                                            style={{ fontWeight: 700 }}
                                                                        >
                                                                            {formatAmount(totals.totalAmount)}
                                                                        </td>
                                                                    );
                                                                }
                                                            }
                                                            return <td key={col}></td>;
                                                        })}
                                                </tr>
                                            </tfoot>
                                        )}
                                    </table>
                                ) : (
                                    <div className="empty-state">
                                        <Icon icon="mdi:database-off-outline" />
                                        <p>No records found for the selected period and filters.</p>
                                    </div>
                                )}
                            </div>

                            <div className="table-pagination-row">
                                <div className="pagination-info">
                                    Showing {data.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0} to {Math.min(currentPage * rowsPerPage, totalItems)} of {totalItems} entries
                                </div>
                                <div className="pagination-controls">
                                    <button
                                        className="pagination-arrow"
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(1)}
                                    >
                                        <Icon icon="mdi:chevron-double-left" />
                                    </button>
                                    <button
                                        className="pagination-arrow"
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(prev => prev - 1)}
                                    >
                                        <Icon icon="mdi:chevron-left" />
                                    </button>

                                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) pageNum = i + 1;
                                        else if (currentPage <= 3) pageNum = i + 1;
                                        else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                        else pageNum = currentPage - 2 + i;

                                        return (
                                            <button
                                                key={pageNum}
                                                className={`pagination-number ${currentPage === pageNum ? "active" : ""}`}
                                                onClick={() => setCurrentPage(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}

                                    <button
                                        className="pagination-arrow"
                                        disabled={currentPage === totalPages || totalPages === 0}
                                        onClick={() => setCurrentPage(prev => prev + 1)}
                                    >
                                        <Icon icon="mdi:chevron-right" />
                                    </button>
                                    <button
                                        className="pagination-arrow"
                                        disabled={currentPage === totalPages || totalPages === 0}
                                        onClick={() => setCurrentPage(totalPages)}
                                    >
                                        <Icon icon="mdi:chevron-double-right" />
                                    </button>
                                </div>
                                <div className="rows-per-page">
                                    <span>Rows per page:</span>
                                    <select
                                        value={rowsPerPage}
                                        onChange={(e) => {
                                            setRowsPerPage(parseInt(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {viewModalOpen && (
                <div className="sr-modal-overlay" onClick={() => setViewModalOpen(false)}>
                    <div className="sr-modal-panel" onClick={(e) => e.stopPropagation()}>
                        <div className="sr-modal-header">
                            <div className="sr-modal-header-left">
                                <Icon icon="mdi:card-account-details-outline" className="sr-modal-header-icon" />
                                <div>
                                    <h2 className="sr-modal-title">
                                        {activeTab === "service-wise"
                                            ? "Service Sales Details"
                                            : activeTab === "membership-wise"
                                                ? "Membership Sales Details"
                                                : "Product Sales Details"}
                                    </h2>
                                    <p className="sr-modal-subtitle">
                                        {activeTab === "staff-summary" ? viewData?.staffName : viewData?.serviceName || viewData?.productName || viewData?.membershipName || "—"}
                                    </p>
                                </div>
                            </div>
                            <button className="sr-modal-close" onClick={() => setViewModalOpen(false)}>
                                <Icon icon="mdi:close" />
                            </button>
                        </div>

                        <div className="sr-modal-body">
                            {viewData && activeTab === "staff-summary" && (
                                <div className="sr-agg-detail">
                                    <div className="sr-agg-hero">
                                        <div className="sr-agg-icon mem">
                                            <Icon icon="mdi:account-tie" />
                                        </div>
                                        <div>
                                            <p className="sr-agg-name">{viewData.staffName || "—"}</p>
                                            <p className="sr-agg-sub">{viewData.branch || "—"} | {viewData.staffCode || "—"}</p>
                                        </div>
                                    </div>
                                    <div className="sr-agg-stats">
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:scissors-cutting" className="stat-icon" />
                                            <span className="stat-label">Services Provided</span>
                                            <span className="stat-value">{viewData.servicesProvided || 0}</span>
                                        </div>
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:package-variant-closed" className="stat-icon" />
                                            <span className="stat-label">Products Sold</span>
                                            <span className="stat-value">{viewData.productsSold || 0}</span>
                                        </div>
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:currency-inr" className="stat-icon" />
                                            <span className="stat-label">Service Revenue</span>
                                            <span className="stat-value">{formatAmount(viewData.serviceRevenue)}</span>
                                        </div>
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:currency-inr" className="stat-icon" />
                                            <span className="stat-label">Product Revenue</span>
                                            <span className="stat-value">{formatAmount(viewData.productRevenue)}</span>
                                        </div>
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:tag-outline" className="stat-icon" />
                                            <span className="stat-label">Discount Given</span>
                                            <span className="stat-value">{formatAmount(viewData.discountGiven)}</span>
                                        </div>
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:finance" className="stat-icon" />
                                            <span className="stat-label">Total Revenue</span>
                                            <span className="stat-value">{formatAmount(viewData.totalRevenue)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {viewData && activeTab === "service-wise" && (
                                <div className="sr-agg-detail">
                                    <div className="sr-agg-hero">
                                        <div className="sr-agg-icon svc">
                                            <Icon icon="mdi:scissors-cutting" />
                                        </div>
                                        <div>
                                            <p className="sr-agg-name">{viewData.serviceName || "—"}</p>
                                            <p className="sr-agg-sub">{viewData.staffName || "—"} | {viewData.branch || "—"}</p>
                                        </div>
                                    </div>
                                    <div className="sr-agg-stats">
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:counter" className="stat-icon" />
                                            <span className="stat-label">Total Sold</span>
                                            <span className="stat-value">{viewData.soldCount || 0}</span>
                                        </div>
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:currency-inr" className="stat-icon" />
                                            <span className="stat-label">Total Revenue</span>
                                            <span className="stat-value">{formatAmount(viewData.totalAmount)}</span>
                                        </div>
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:chart-line" className="stat-icon" />
                                            <span className="stat-label">Avg. Price / Unit</span>
                                            <span className="stat-value">
                                                {viewData.soldCount > 0
                                                    ? formatAmount(viewData.totalAmount / viewData.soldCount)
                                                    : "0.00"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {viewData && activeTab === "product-wise" && (
                                <div className="sr-agg-detail">
                                    <div className="sr-agg-hero">
                                        <div className="sr-agg-icon prod">
                                            <Icon icon="mdi:package-variant-closed" />
                                        </div>
                                        <div>
                                            <p className="sr-agg-name">{viewData.productName || "—"}</p>
                                            <p className="sr-agg-sub">{viewData.staffName || "—"} | {viewData.branch || "—"}</p>
                                        </div>
                                    </div>
                                    <div className="sr-agg-stats">
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:counter" className="stat-icon" />
                                            <span className="stat-label">Total Sold</span>
                                            <span className="stat-value">{viewData.soldCount || 0}</span>
                                        </div>
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:currency-inr" className="stat-icon" />
                                            <span className="stat-label">Total Revenue</span>
                                            <span className="stat-value">{formatAmount(viewData.totalAmount)}</span>
                                        </div>
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:chart-line" className="stat-icon" />
                                            <span className="stat-label">Avg. Price / Unit</span>
                                            <span className="stat-value">
                                                {viewData.soldCount > 0
                                                    ? formatAmount(viewData.totalAmount / viewData.soldCount)
                                                    : "0.00"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {(viewData && activeTab === "membership-wise") && (
                                <div className="membership-modal-wrapper">

                                    {/* Top Profile Section */}
                                    <div className="membership-top-section">

                                        <div className="membership-profile-left">

                                            <div className="membership-avatar">
                                                {viewData.staffName?.charAt(0)?.toUpperCase()}
                                            </div>

                                            <div className="membership-user-info">
                                                <h2>{viewData.staffName || "—"}</h2>

                                                <p>
                                                    <Icon icon="mdi:store-outline" />
                                                    {viewData.branch || "Main Branch"}
                                                </p>

                                                <div className="membership-badge">
                                                    <Icon icon="mdi:card-account-details-outline" />
                                                    {viewData.membershipName || "Membership"}
                                                </div>
                                            </div>

                                        </div>

                                        <div className="membership-total-revenue">
                                            <span>Total Revenue</span>

                                            <h1>
                                                {formatAmount(viewData.totalAmount || 0)}
                                            </h1>
                                        </div>

                                    </div>

                                    {/* Stats Cards */}
                                    <div className="membership-stats-grid">

                                        <div className="membership-stat-card purple">
                                            <div className="membership-stat-icon">
                                                <Icon icon="mdi:counter" />
                                            </div>

                                            <div>
                                                <span>Total Memberships</span>
                                                <h3>{viewData.soldCount || 0}</h3>
                                            </div>
                                        </div>

                                        <div className="membership-stat-card green">
                                            <div className="membership-stat-icon">
                                                <Icon icon="mdi:currency-inr" />
                                            </div>

                                            <div>
                                                <span>Total Sales</span>
                                                <h3>
                                                    {formatAmount(viewData.totalAmount || 0)}
                                                </h3>
                                            </div>
                                        </div>

                                        <div className="membership-stat-card orange">
                                            <div className="membership-stat-icon">
                                                <Icon icon="mdi:chart-line" />
                                            </div>

                                            <div>
                                                <span>Avg. Sale Value</span>

                                                <h3>
                                                    {
                                                        viewData.soldCount > 0
                                                            ? formatAmount(
                                                                viewData.totalAmount /
                                                                viewData.soldCount
                                                            )
                                                            : "0.00"
                                                    }
                                                </h3>
                                            </div>
                                        </div>

                                    </div>

                                    {/* Membership Detail Box */}
                                    <div className="membership-detail-box">

                                        <div className="membership-detail-row">
                                            <span>Membership Name</span>
                                            <strong>{viewData.membershipName || "-"}</strong>
                                        </div>

                                        <div className="membership-detail-row">
                                            <span>Staff Name</span>
                                            <strong>{viewData.staffName || "-"}</strong>
                                        </div>

                                        <div className="membership-detail-row">
                                            <span>Branch</span>
                                            <strong>{viewData.branch || "-"}</strong>
                                        </div>

                                        <div className="membership-detail-row">
                                            <span>Total Sold</span>
                                            <strong>{viewData.soldCount || 0}</strong>
                                        </div>

                                    </div>

                                </div>
                            )}

                        </div>

                        <div className="sr-modal-footer">
                            <button className="sr-modal-btn-close" onClick={() => setViewModalOpen(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StaffSalesReport;
